/**
 * Tasks Admin View
 *
 * Monitor and manage outbound tasks: view task status, create new tasks,
 * review responses, trigger escalations, and view task history.
 */

import { html, nothing } from "lit";
import type { TemplateSummary } from "../controllers/igloobot.ts";
import type { ProjectEntry } from "./projects.ts";

export type TaskEntry = {
  id: string;
  projectId: string;
  projectName?: string;
  assignedUserName?: string;
  type: string;
  title: string;
  description: string | null;
  status: string;
  priority: number;
  retryCount: number;
  maxRetries: number;
  scheduledAt: string | null;
  contactedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  responseCount?: number;
};

export type AgentOption = { id: string; name?: string };

export type TasksAdminProps = {
  loading: boolean;
  error: string | null;
  tasks: TaskEntry[];
  statusFilter: string | null;
  typeFilter: string | null;
  projectFilter: string | null;
  agentFilter: string | null;
  selectedTask: TaskEntry | null;
  createMode: boolean;
  createForm: Record<string, unknown> | null;
  /** task templates for the create-form picklist. */
  taskTemplates?: TemplateSummary[];
  projects: ProjectEntry[];
  agents: AgentOption[];
  onRefresh: () => void;
  onFilterStatus: (status: string | null) => void;
  onFilterType: (type: string | null) => void;
  onFilterProject: (projectId: string | null) => void;
  onFilterAgent: (agentId: string | null) => void;
  onSelectTask: (id: string) => void;
  onCreateTask: () => void;
  onCancelCreate: () => void;
  onCreateFormChange: (field: string, value: unknown) => void;
  onSaveNewTask: () => void;
  onCancelTask: (id: string) => void;
  onRetryTask: (id: string) => void;
};

const STATUS_COLORS: Record<string, string> = {
  pending: "",
  scheduled: "",
  contacted: "pill--ok",
  awaiting_response: "pill--ok",
  in_progress: "pill--ok",
  completed: "pill--ok",
  failed: "pill--danger",
  escalated: "pill--danger",
  cancelled: "",
};

const STATUS_OPTIONS = [
  "pending",
  "scheduled",
  "contacted",
  "awaiting_response",
  "in_progress",
  "completed",
  "failed",
  "escalated",
  "cancelled",
];

const TYPE_OPTIONS = [
  "data_collection",
  "task_assignment",
  "progress_check",
  "status_update",
  "reminder",
  "custom",
];

const inputStyle =
  "width:100%;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);";

export function renderTasksAdmin(props: TasksAdminProps) {
  const filteredTasks = props.tasks.filter((t) => {
    if (props.statusFilter && t.status !== props.statusFilter) return false;
    if (props.typeFilter && t.type !== props.typeFilter) return false;
    return true;
  });

  // Status counts
  const statusCounts = new Map<string, number>();
  for (const task of props.tasks) {
    statusCounts.set(task.status, (statusCounts.get(task.status) ?? 0) + 1);
  }

  return html`
    <section>
      <!-- Stats Row -->
      <div class="tasks-stat-grid">
        <div class="stat" style="cursor:pointer;" @click=${() => props.onFilterStatus(null)}>
          <div class="stat-value">${props.tasks.length}</div>
          <div class="stat-label">Total</div>
        </div>
        <div class="stat" style="cursor:pointer;" @click=${() => props.onFilterStatus("awaiting_response")}>
          <div class="stat-value">${statusCounts.get("awaiting_response") ?? 0}</div>
          <div class="stat-label">Awaiting</div>
        </div>
        <div class="stat" style="cursor:pointer;" @click=${() => props.onFilterStatus("in_progress")}>
          <div class="stat-value">${statusCounts.get("in_progress") ?? 0}</div>
          <div class="stat-label">In Progress</div>
        </div>
        <div class="stat" style="cursor:pointer;" @click=${() => props.onFilterStatus("completed")}>
          <div class="stat-value">${statusCounts.get("completed") ?? 0}</div>
          <div class="stat-label">Completed</div>
        </div>
        <div class="stat" style="cursor:pointer;" @click=${() => props.onFilterStatus("failed")}>
          <div class="stat-value">${(statusCounts.get("failed") ?? 0) + (statusCounts.get("escalated") ?? 0)}</div>
          <div class="stat-label">Failed/Escalated</div>
        </div>
      </div>

      <!-- Create Task Form -->
      ${props.createMode ? renderCreateForm(props) : nothing}

      <!-- Filters & Actions -->
      <div class="card" style="margin-bottom:16px;">
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <button class="btn btn--sm" @click=${props.onRefresh} ?disabled=${props.loading}>
            ${props.loading ? "Loading..." : "Refresh"}
          </button>
          <button class="btn btn--sm primary" @click=${props.onCreateTask}>
            + New Task
          </button>

          <select
            style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
            @change=${(e: Event) => {
              const v = (e.target as HTMLSelectElement).value;
              props.onFilterStatus(v || null);
            }}
          >
            <option value="" ?selected=${!props.statusFilter}>All Statuses</option>
            ${STATUS_OPTIONS.map(
              (s) =>
                html`<option value=${s} ?selected=${props.statusFilter === s}>${s.replace(/_/g, " ")}</option>`,
            )}
          </select>

          <select
            style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
            @change=${(e: Event) => {
              const v = (e.target as HTMLSelectElement).value;
              props.onFilterType(v || null);
            }}
          >
            <option value="" ?selected=${!props.typeFilter}>All Types</option>
            ${TYPE_OPTIONS.map(
              (t) =>
                html`<option value=${t} ?selected=${props.typeFilter === t}>${t.replace(/_/g, " ")}</option>`,
            )}
          </select>

          <select
            style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
            @change=${(e: Event) => {
              const v = (e.target as HTMLSelectElement).value;
              props.onFilterProject(v || null);
            }}
          >
            <option value="" ?selected=${!props.projectFilter}>All Projects</option>
            ${props.projects.map(
              (p) =>
                html`<option value=${p.id} ?selected=${props.projectFilter === p.id}>${p.name}</option>`,
            )}
          </select>

          <select
            style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
            @change=${(e: Event) => {
              const v = (e.target as HTMLSelectElement).value;
              props.onFilterAgent(v || null);
            }}
          >
            <option value="" ?selected=${!props.agentFilter}>All Agents</option>
            ${props.agents.map(
              (a) =>
                html`<option value=${a.id} ?selected=${props.agentFilter === a.id}>${a.name ?? a.id}</option>`,
            )}
          </select>
        </div>
      </div>

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      <!-- Task Detail Panel -->
      ${props.selectedTask ? renderTaskDetail(props) : nothing}

      <!-- Task List -->
      <div class="card">
        ${
          filteredTasks.length === 0
            ? html`
                <div class="muted" style="padding: 16px">No tasks match the current filters.</div>
              `
            : html`
              <div class="tasks-table-wrap">
                <table style="width:100%;border-collapse:collapse;font-size:0.9em;min-width:600px;">
                  <thead>
                    <tr style="border-bottom:1px solid var(--border);text-align:left;">
                      <th style="padding:8px;">Title</th>
                      <th style="padding:8px;">Type</th>
                      <th style="padding:8px;">Status</th>
                      <th style="padding:8px;">Assigned To</th>
                      <th style="padding:8px;">Created</th>
                      <th style="padding:8px;">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${filteredTasks.map(
                      (task) => html`
                        <tr
                          style="border-bottom:1px solid var(--border);cursor:pointer;${props.selectedTask?.id === task.id ? "background:var(--bg-inset);" : ""}"
                          @click=${() => props.onSelectTask(task.id)}
                        >
                          <td style="padding:8px;">
                            <strong>${task.title}</strong>
                            ${
                              task.responseCount
                                ? html`<span class="muted" style="font-size:0.8em;"> (${task.responseCount} responses)</span>`
                                : nothing
                            }
                          </td>
                          <td style="padding:8px;">
                            <span class="pill pill--sm">${task.type.replace(/_/g, " ")}</span>
                          </td>
                          <td style="padding:8px;">
                            <span class="pill pill--sm ${STATUS_COLORS[task.status] ?? ""}">
                              ${task.status.replace(/_/g, " ")}
                            </span>
                          </td>
                          <td style="padding:8px;">${task.assignedUserName ?? "Unassigned"}</td>
                          <td style="padding:8px;" class="muted">${task.createdAt.split("T")[0]}</td>
                          <td style="padding:8px;" @click=${(e: Event) => e.stopPropagation()}>
                            ${
                              task.status === "failed" || task.status === "escalated"
                                ? html`<button class="btn btn--sm" @click=${() => props.onRetryTask(task.id)}>Retry</button>`
                                : nothing
                            }
                            ${
                              task.status !== "completed" && task.status !== "cancelled"
                                ? html`<button class="btn btn--sm" @click=${() => props.onCancelTask(task.id)}>Cancel</button>`
                                : nothing
                            }
                          </td>
                        </tr>
                      `,
                    )}
                  </tbody>
                </table>
              </div>
            `
        }
      </div>
    </section>
  `;
}

function renderCreateForm(props: TasksAdminProps) {
  const form = props.createForm;
  if (!form) return nothing;

  const templates = props.taskTemplates ?? [];
  const applyTemplate = (id: string) => {
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) {
      return;
    }
    const p = tpl.payload ?? {};
    props.onCreateFormChange("title", tpl.description || tpl.name);
    if (typeof p.type === "string") {
      props.onCreateFormChange("type", p.type);
    }
    props.onCreateFormChange("description", tpl.coreInstruction);
  };

  return html`
    <div class="card" style="margin-bottom:16px;border-color:var(--accent);">
      <div class="card-title">Create New Task</div>
      <div style="display:grid;gap:12px;margin-top:12px;">
        ${
          templates.length > 0
            ? html`
              <div class="field">
                <label>Template (optional)</label>
                <select
                  style="${inputStyle}"
                  @change=${(e: Event) => applyTemplate((e.target as HTMLSelectElement).value)}
                >
                  <option value="">— none —</option>
                  ${templates.map(
                    (t) => html`<option value=${t.id}>${t.description || t.name}</option>`,
                  )}
                </select>
              </div>`
            : nothing
        }
        <div class="field">
          <label>Project</label>
          <select
            style="${inputStyle}"
            @change=${(e: Event) => props.onCreateFormChange("projectId", (e.target as HTMLSelectElement).value)}
          >
            ${
              props.projects.length === 0
                ? html`
                    <option value="">No projects available</option>
                  `
                : props.projects.map(
                    (p) =>
                      html`<option value=${p.id} ?selected=${form.projectId === p.id}>${p.name}</option>`,
                  )
            }
          </select>
        </div>
        <div class="field">
          <label>Title</label>
          <input
            type="text"
            .value=${(form.title as string) ?? ""}
            @input=${(e: Event) => props.onCreateFormChange("title", (e.target as HTMLInputElement).value)}
            placeholder="Task title"
            style="${inputStyle}"
          />
        </div>
        <div class="field">
          <label>Type</label>
          <select
            style="${inputStyle}"
            @change=${(e: Event) => props.onCreateFormChange("type", (e.target as HTMLSelectElement).value)}
          >
            ${TYPE_OPTIONS.map(
              (t) =>
                html`<option value=${t} ?selected=${form.type === t}>${t.replace(/_/g, " ")}</option>`,
            )}
          </select>
        </div>
        <div class="field">
          <label>Description (optional)</label>
          <textarea
            .value=${(form.description as string) ?? ""}
            @input=${(e: Event) => props.onCreateFormChange("description", (e.target as HTMLTextAreaElement).value)}
            placeholder="Task description..."
            rows="3"
            style="${inputStyle}font-family:monospace;font-size:0.9em;resize:vertical;"
          ></textarea>
        </div>
        <div style="display:flex;gap:8px;">
          <button class="btn btn--sm primary" @click=${props.onSaveNewTask}>Create Task</button>
          <button class="btn btn--sm" @click=${props.onCancelCreate}>Cancel</button>
        </div>
      </div>
    </div>
  `;
}

function renderTaskDetail(props: TasksAdminProps) {
  const task = props.selectedTask;
  if (!task) return nothing;

  return html`
    <div class="card" style="margin-bottom:16px;">
      <div class="card-title" style="display:flex;justify-content:space-between;align-items:center;">
        <span>${task.title}</span>
        <button class="btn btn--sm" @click=${() => props.onSelectTask(task.id)}>Close</button>
      </div>

      <div class="detail-stat-grid" style="margin:12px 0;">
        <div class="stat">
          <div class="stat-value">
            <span class="pill pill--sm ${STATUS_COLORS[task.status] ?? ""}">${task.status.replace(/_/g, " ")}</span>
          </div>
          <div class="stat-label">Status</div>
        </div>
        <div class="stat">
          <div class="stat-value">${task.retryCount}/${task.maxRetries}</div>
          <div class="stat-label">Retries</div>
        </div>
        <div class="stat">
          <div class="stat-value">${task.responseCount ?? 0}</div>
          <div class="stat-label">Responses</div>
        </div>
      </div>

      <div style="display:grid;gap:8px;font-size:0.9em;">
        <div><strong>Type:</strong> <span class="pill pill--sm">${task.type.replace(/_/g, " ")}</span></div>
        <div><strong>Assigned to:</strong> ${task.assignedUserName ?? "Unassigned"}</div>
        ${task.description ? html`<div><strong>Description:</strong> ${task.description}</div>` : nothing}
        <div class="muted" style="font-size:0.85em;">
          Created: ${task.createdAt.split("T")[0]}
          ${task.scheduledAt ? html` | Scheduled: ${task.scheduledAt.split("T")[0]}` : nothing}
          ${task.contactedAt ? html` | Contacted: ${task.contactedAt.split("T")[0]}` : nothing}
          ${task.completedAt ? html` | Completed: ${task.completedAt.split("T")[0]}` : nothing}
        </div>
      </div>

      <div style="display:flex;gap:8px;margin-top:12px;">
        ${
          task.status === "failed" || task.status === "escalated"
            ? html`<button class="btn btn--sm primary" @click=${() => props.onRetryTask(task.id)}>Retry Task</button>`
            : nothing
        }
        ${
          task.status !== "completed" && task.status !== "cancelled"
            ? html`<button class="btn btn--sm" @click=${() => props.onCancelTask(task.id)}>Cancel Task</button>`
            : nothing
        }
      </div>
    </div>
  `;
}
